import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_CONFIG } from '../config/api';

/**
 * 18+ age-gate. Shown on first visit to ANY page when the admin has
 * enabled it in Site Settings → Platform → "Age gate (18+)". Decision
 * is persisted in localStorage so returning visitors don't see it twice.
 *
 * Wiring:
 *   - /api/site-config returns { age_gate: { enabled, decline_url } }. Title/
 *     message are static, localized strings (ageGate.title/message in the
 *     `common` i18n namespace) — not admin-configurable — so the modal always
 *     matches the site language.
 *   - First render reads the cached accepted-flag synchronously (so the
 *     modal never flashes for someone who already accepted)
 *   - In parallel a fetch confirms the admin still has the gate enabled
 *     (if they turned it off after a visitor accepted, we never bother
 *     them again either way)
 *
 * Decline = window.location.href to admin-configured URL (default google.com).
 * Accept = stores localStorage.age_gate_accepted_v1 + closes.
 */
const STORAGE_KEY = 'age_gate_accepted_v1';

const isAccepted = () => {
  try { return localStorage.getItem(STORAGE_KEY) === 'true'; }
  catch (_) { return false; }
};

const markAccepted = () => {
  try { localStorage.setItem(STORAGE_KEY, 'true'); } catch (_) {}
};

export default function AgeGateModal() {
  const { t } = useTranslation();
  // Start hidden by default. Only flip to visible after the server
  // confirms the gate is enabled AND the visitor hasn't accepted it.
  // Starting hidden avoids a flash for the common case (gate disabled).
  const [config, setConfig] = useState(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (isAccepted()) return; // never even ask the server

    let cancelled = false;
    axios
      .get(`${API_CONFIG.BASE_URL}/site-config`, { timeout: 5000 })
      .then((res) => {
        if (cancelled) return;
        const gate = res.data?.data?.age_gate || {};
        if (!gate.enabled) return; // admin has it off → no modal
        setConfig({
          declineUrl: gate.decline_url || 'https://www.google.com',
        });
        setShow(true);
        // Lock page scroll while the modal is open.
        try { document.documentElement.style.overflow = 'hidden'; } catch (_) {}
      })
      .catch(() => { /* gate stays hidden on network blip */ });

    return () => {
      cancelled = true;
      try { document.documentElement.style.overflow = ''; } catch (_) {}
    };
  }, []);

  if (!show || !config) return null;

  const accept = () => {
    markAccepted();
    setShow(false);
    try { document.documentElement.style.overflow = ''; } catch (_) {}
  };

  const decline = () => {
    // Don't store the decline — if the same visitor changes their mind
    // they can come back. Just navigate away.
    window.location.href = config.declineUrl;
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 2147483647, // top of the world — beats any in-app modal
        fontFamily: 'ui-sans-serif, system-ui, Inter, -apple-system, "Segoe UI", Helvetica, Arial, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 440,
          width: '100%',
          background: '#fff',
          borderRadius: 18,
          padding: '32px 28px',
          boxShadow: '0 30px 80px -20px rgba(0, 0, 0, 0.6)',
          textAlign: 'center',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            width: 64,
            height: 64,
            margin: '0 auto 16px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #ec4899, #a855f7)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 24,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            boxShadow: '0 14px 32px -10px rgba(168, 85, 247, 0.55)',
          }}
        >
          18+
        </div>

        <h2
          id="age-gate-title"
          style={{
            margin: '0 0 10px',
            fontSize: 20,
            fontWeight: 700,
            color: '#0f172a',
            letterSpacing: '-0.01em',
          }}
        >
          {t('ageGate.title')}
        </h2>

        <p
          style={{
            margin: '0 0 22px',
            fontSize: 14.5,
            lineHeight: 1.55,
            color: '#475569',
          }}
        >
          {t('ageGate.message')}
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <button
            type="button"
            onClick={decline}
            style={{
              padding: '11px 14px',
              borderRadius: 12,
              border: '1px solid #e2e8f0',
              background: '#fff',
              color: '#475569',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {t('ageGate.declineButton')}
          </button>
          <button
            type="button"
            onClick={accept}
            style={{
              padding: '11px 14px',
              borderRadius: 12,
              border: 0,
              background: 'linear-gradient(135deg, #ec4899, #a855f7)',
              color: '#fff',
              fontSize: 14,
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 10px 24px -10px rgba(168, 85, 247, 0.55)',
            }}
          >
            {t('ageGate.acceptButton')}
          </button>
        </div>

        <p
          style={{
            margin: '16px 0 0',
            fontSize: 11,
            color: '#94a3b8',
            lineHeight: 1.5,
          }}
        >
          {t('ageGate.disclaimer')}
        </p>
      </div>
    </div>
  );
}
